import { useMemo, useState } from "react";
import { Box, Button, Typography, Avatar, Stack, Chip, TextField, CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { colors, alphaVar } from "../../../../ui/colors";
import { useTransferAvailableQuery } from "./hooks/useTransferAvailableQuery";
import { useVirtualAssistant } from "../../../context/VirtualAssistantContext";
import { TransferAvailableAgent } from "./types/transfer.types";
import { STATUS_CONFIG } from "./config/available-status.config";
import { useAppSelector } from "../../../../../redux/hooks/redux";

interface Props {
    onClose?: () => void;
}

const resolveTransferErrorMessage = (error: unknown): string => {
    const backendMessage = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;

    if (Array.isArray(backendMessage)) return backendMessage.join(", ");
    if (backendMessage) return backendMessage;
    if (error instanceof Error) return error.message;

    return "Transfer failed. Please try again.";
};

export const TransferPanel = ({ onClose }: Props) => {
    const { companiesState, handleTransfer } = useVirtualAssistant();
    const uid = useAppSelector((state) => state.userState.uid);

    const activeCallKey = useMemo(() => {
        const activeEntries = Array.from(companiesState.entries()).filter(
            ([, item]) => item.activeCall
        );

        return activeEntries.length ? activeEntries[activeEntries.length - 1][0] : undefined;
    }, [companiesState]);

    const transferCompanyId = useMemo(() => {
        if (!activeCallKey) return undefined;

        const companyId = companiesState.get(activeCallKey)?.companyId;

        return companyId ? String(companyId) : undefined;
    }, [activeCallKey, companiesState]);

    const { data, isLoading, isError } = useTransferAvailableQuery({
        companyId: transferCompanyId ? Number(transferCompanyId) : undefined,
        enabled: !!transferCompanyId,
    });


    const [search, setSearch] = useState("");
    const [selectedUid, setSelectedUid] = useState<string | null>(null);
    const [isTransferring, setIsTransferring] = useState(false);
    const [transferError, setTransferError] = useState<string | null>(null);

    const agents = useMemo<TransferAvailableAgent[]>(() => data?.agents ?? [], [data]);

    const filteredAgents = useMemo(() => {
        return agents.filter((agent) => {
            if (agent.agentUid === uid) return false;
            const full = `${agent.firstName} ${agent.lastName} ${agent.email}`.toLowerCase();
            return full.includes(search.toLowerCase());
        });
    }, [agents, search, uid]);

    const selectedAgent = useMemo(
        () => filteredAgents.find((agent) => agent.agentUid === selectedUid) ?? null,
        [filteredAgents, selectedUid]
    );

    const canTransfer = !!selectedAgent && selectedAgent.status === "available" && !!activeCallKey;

    const handleTransferClick = async () => {
        if (!selectedUid || !activeCallKey) return;
        try {
            setIsTransferring(true);
            setTransferError(null);

            await handleTransfer(activeCallKey, selectedUid);
            onClose?.();
        } catch (err) {
            setTransferError(resolveTransferErrorMessage(err));
        } finally {
            setIsTransferring(false);
        }
    };

    return (
        <Box sx={{ textAlign: "left" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.76rem",
                        fontWeight: 600,
                        color: colors.gray[300],
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                    }}
                >
                    Transfer to agent
                </Typography>

                {
                    onClose && (
                        <IconButton
                            size="small"
                            onClick={onClose}
                            sx={{
                                color: colors.gray[400],
                                p: 0.5,
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )
                }
            </Box>

            <TextField
                fullWidth
                size="small"
                placeholder="Search agents..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                    mb: 1,
                    "& .MuiInputBase-input": { fontSize: "0.8rem" },
                }}
            />

            {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={18} />
                </Box>
            )}

            {isError && (
                <Typography
                    sx={{
                        fontSize: "0.8rem",
                        color: colors.danger?.base ?? "#dc2626",
                        textAlign: "center",
                        py: 1.5,
                    }}
                >
                    Could not load agents
                </Typography>
            )}

            {!isLoading && !isError && (
                <Stack spacing={0.5}>
                    {filteredAgents.length === 0 && (
                        <Typography
                            sx={{
                                fontSize: "0.8rem",
                                color: colors.gray[300],
                                textAlign: "center",
                                py: 1.5,
                            }}
                        >
                            No agents found
                        </Typography>
                    )}

                    {filteredAgents.map((agent) => {
                        const cfg = STATUS_CONFIG[agent.status];
                        const isSelected = selectedUid === agent.agentUid;

                        return (
                            <Box
                                key={agent.agentUid}
                                onClick={() => setSelectedUid(isSelected ? null : agent.agentUid)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: "8px 10px",
                                    borderRadius: 1.5,
                                    cursor: "pointer",
                                    background: isSelected ? alphaVar("#2563eb", 0.08) : "transparent",
                                    "&:hover": {
                                        background: isSelected
                                            ? alphaVar("#2563eb", 0.1)
                                            : alphaVar(colors.gray[100], 0.5),
                                    },
                                    transition: "background 0.12s",
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        fontSize: "0.72rem",
                                        fontWeight: 500,
                                        bgcolor: alphaVar(colors.gray[300], 0.3),
                                        color: colors.gray[500],
                                    }}
                                >
                                    {(agent.firstName?.[0] || "?").toUpperCase()}
                                    {(agent.lastName?.[0] || "").toUpperCase()}
                                </Avatar>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "0.82rem",
                                            fontWeight: 600,
                                            color: colors.gray[500],
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {agent.firstName} {agent.lastName}
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: "0.72rem", color: colors.gray[300] }}
                                        noWrap
                                    >
                                        {agent.email}
                                    </Typography>
                                </Box>

                                <Chip
                                    label={cfg.label}
                                    size="small"
                                    sx={{
                                        fontSize: "0.68rem",
                                        fontWeight: 600,
                                        height: 20,
                                        bgcolor: cfg.bg,
                                        color: cfg.color,
                                        border: "none",
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {transferError && (
                <Typography
                    sx={{
                        mt: 1.5,
                        fontSize: "0.76rem",
                        color: colors.danger?.base ?? "#dc2626",
                        textAlign: "center",
                    }}
                >
                    {transferError}
                </Typography>
            )}

            <Button
                fullWidth
                disabled={!canTransfer || isTransferring}
                onClick={handleTransferClick}
                sx={{
                    mt: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    backgroundColor: canTransfer ? "#2563eb" : undefined,
                    color: canTransfer ? "white" : undefined,
                    "&:hover": {
                        backgroundColor: canTransfer ? "#1d4ed8" : undefined,
                    },
                    "&.Mui-disabled": {
                        backgroundColor: alphaVar(colors.gray[300], 0.2),
                        color: colors.gray[300],
                    },
                }}
            >
                {isTransferring
                    ? "Transferring..."
                    : canTransfer
                        ? `Transfer to ${selectedAgent?.firstName} ${selectedAgent?.lastName}`
                        : selectedUid
                            ? "Agent unavailable"
                            : "Select an agent"}
            </Button>
        </Box>
    );
};